import { type ReactNode, useId, useRef, useState } from 'react';
import { Icon } from './Icon';

/**
 * Tooltip primitive (design-language §7): consistent, theme-aware, crisp + small. ~240ms hover
 * delay; long-press on touch (kiosk). Explains metrics/status/controls so admin surfaces stay
 * clean *and* legible (spend, credits, data vintage, health dots, escalation flags).
 *
 * Wrap any trigger:  <Tooltip label="Month-to-date provider spend">…</Tooltip>
 * Or use <InfoTip label="…" /> for a standalone line-info affordance next to a metric.
 */
export function Tooltip({
  label,
  children,
  side = 'top',
}: {
  label: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom';
}) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  const show = (delay = 240) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(false);
  };

  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={() => show()}
      onMouseLeave={hide}
      onFocus={() => show(0)}
      onBlur={hide}
      onTouchStart={() => show(350)}
      onTouchEnd={hide}
      onTouchCancel={hide}
    >
      <span aria-describedby={open ? id : undefined}>{children}</span>
      {open && (
        <span
          role="tooltip"
          id={id}
          className={`pointer-events-none absolute left-1/2 -translate-x-1/2 ${
            side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          } z-[var(--z-tooltip)] w-max max-w-[15rem] rounded-md border border-line-strong bg-elevated px-2.5 py-1.5 text-[11px] leading-snug text-ink shadow-soft`}
          style={{ animation: 'reveal var(--dur-1) var(--ease-out) both' }}
        >
          {label}
        </span>
      )}
    </span>
  );
}

/** Standalone info affordance — a small line `info` glyph that reveals an explanation. */
export function InfoTip({ label, className }: { label: ReactNode; className?: string }) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label="More information"
        className={`inline-grid place-items-center rounded-full text-subtle transition-colors duration-1 ease-physical hover:text-brass ${className ?? ''}`}
      >
        <Icon name="info" size={15} />
      </button>
    </Tooltip>
  );
}
